import DatePicker from 'react-datepicker';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { useCompany } from '../../../hooks/use-company';
import { useAuth } from '../../../hooks/use-auth';
import StyledError from '../../../components/common/form/styled-error';
import { strings } from '../../../localization/strings';
import CommonButtonDefault from '../../../components/common/buttons/default';
import * as companiesService from '../../../services/api/companies';
import { getUnixTimestamp, isValidSubscriptionDates } from '../../../services/formatter/date';
import AlertPrimary from '../../../components/common/alerts/alert-primary';
import StyledSwitch from '../../../components/common/form/styled-switch';
import Tooltip from '../../../components/common/tooltip';

type InputsPressrelease = {
  video_premium_active: number;
  video_premium_from: Date;
  video_premium_to: Date;
};

function ServicesPremiumVideoForm() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const { token } = useAuth();
  const { updateCompany, companyDetails } = useCompany();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<InputsPressrelease>({
    defaultValues: {
      video_premium_active: companyDetails.video_premium_active,
      video_premium_from: companyDetails.video_premium_from
        ? new Date(companyDetails.video_premium_from * 1000)
        : undefined,
      video_premium_to: companyDetails.video_premium_to ? new Date(companyDetails.video_premium_to * 1000) : undefined,
    },
  });

  const onSubmitPressRelease: SubmitHandler<InputsPressrelease> = async (data) => {
    try {
      if (companyDetails?.id) {
        setIsLoading(true);

        const formData = {
          ...data,
          video_premium_active: data.video_premium_active ? 1 : 0,
          video_premium_from:
            data.video_premium_from && data.video_premium_active ? getUnixTimestamp(data.video_premium_from) : null,
          video_premium_to:
            data.video_premium_to && data.video_premium_active ? getUnixTimestamp(data.video_premium_to) : null,
        };

        const res = await companiesService.update(token, companyDetails?.id, formData);

        if (res) {
          setIsLoading(false);
          setIsSubmitSuccessful(true);

          updateCompany(formData);

          setTimeout(() => {
            setIsSubmitSuccessful(false);
          }, 2000);
        }
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setError(e.message);
      }
    }
  };

  return (
    <>
      {error && (
        <div className="max-w-7xl mx-auto my-5 mt-8">
          <AlertPrimary type="danger" text={error} />
        </div>
      )}
      {isSubmitSuccessful && (
        <div className="mb-2 mt-8">
          <AlertPrimary size="xs" type="success" text="Sėkmingai atnaujinta" />
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmitPressRelease)}>
        <div className="bg-white sm:rounded-lg overflow-hidden ring-1 ring-gray-900/5 mt-8">
          <div className="p-4 font-medium leading-6 bg-gray-50 text-gray-900 flex flex-row">
            <h3>{strings.companies.services.premiumVideo}</h3>
            <div className="ml-auto">
              <CommonButtonDefault type="submit" isLoading={isLoading} xs primary>
                {strings.button.save}
              </CommonButtonDefault>
            </div>
            <div className="ml-4">
              <Tooltip
                disabled={!isValidSubscriptionDates(watch('video_premium_from'), watch('video_premium_to'))}
                title={
                  <StyledSwitch
                    name="video_premium_active"
                    control={control}
                    disabled={isValidSubscriptionDates(watch('video_premium_from'), watch('video_premium_to'))}
                  />
                }
              >
                {strings.companies.services.disableServiceTooltip}
              </Tooltip>
            </div>
          </div>

          {watch('video_premium_active') === 1 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-x-4 p-4">
              <div className="col-span-1">
                <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                  Data nuo
                </label>
                <div className="mt-1">
                  <Controller
                    control={control}
                    name="video_premium_from"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        // monthsShown={2}
                        showYearDropdown
                        dateFormat="yyyy-MM-dd"
                        isClearable
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        selected={field.value ? new Date(field.value) : undefined}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        disabledKeyboardNavigation
                      />
                    )}
                  />
                  {errors.video_premium_from && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                </div>
              </div>
              <div className="col-span-1">
                <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                  Data iki
                </label>
                <div className="mt-1">
                  <Controller
                    control={control}
                    name="video_premium_to"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        showYearDropdown
                        dateFormat="yyyy-MM-dd"
                        isClearable
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        selected={field.value ? new Date(field.value) : undefined}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        disabledKeyboardNavigation
                      />
                    )}
                  />
                  {errors.video_premium_to && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
}

export default ServicesPremiumVideoForm;
